import React, { useState } from "react";
import { supabase } from "../../../services/supabaseClient";

type SearchLabsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelectLab: (lab: any) => void; // ✅ new prop
};

const SearchLabsModal = ({ isOpen, onClose, onSelectLab }: SearchLabsModalProps) => {
  const [results, setResults] = useState(() => []);
  const [cpscId, setCpscId] = useState("");
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSearch = async () => {
    setLoading(true);
    try {
      // 1️⃣ Query certified_labs
      let certifiedQuery = supabase
        .from("certified_labs")
        .select("id, name, cpsc_id, address_line_1, city, country");

      if (cpscId.trim()) {
        certifiedQuery = certifiedQuery.ilike("cpsc_id", `%${cpscId.trim()}%`);
      }

      if (name.trim()) {
        certifiedQuery = certifiedQuery.ilike("name", `%${name.trim()}%`);
      }

      if (address.trim()) {
        certifiedQuery = certifiedQuery.or(
          `address_line_1.ilike.%${address.trim()}%, city.ilike.%${address.trim()}%, country.ilike.%${address.trim()}%`
        );
      }

      const { data: certifiedData, error: certifiedError } = await certifiedQuery.limit(50);
      if (certifiedError) console.error("Certified labs error:", certifiedError);

      // 2️⃣ Query test_labs
      let testQuery = supabase
        .from("test_labs")
        .select("id, name, address_line_1, city, country");

      if (name.trim()) {
        testQuery = testQuery.ilike("name", `%${name.trim()}%`);
      }

      if (address.trim()) {
        testQuery = testQuery.or(
          `address_line_1.ilike.%${address.trim()}%, city.ilike.%${address.trim()}%, country.ilike.%${address.trim()}%`
        );
      }

      const { data: testData, error: testError } = await testQuery.limit(50);
      if (testError) console.error("Test labs error:", testError);

      // 3️⃣ Combine and label results
      const merged = [
        ...(certifiedData || []).map((lab: any) => ({
          ...lab,
          source: "certified",
        })),
        ...(testData || []).map((lab: any) => ({
          ...lab,
          source: "user",
        })),
      ];

      setResults(merged);
    } catch (err) {
      console.error("Unexpected search error:", err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="data-modal-overlay" onClick={onClose}>
      <div className="data-modal" onClick={(e) => e.stopPropagation()}>
        <div className="data-modal-header">
          <h2 className="data-modal-title">Search Labs</h2>
          <button className="modal-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div className="data-modal-body">
          <div className="form-grid">
            {/* CPSC ID */}
            <div className="form-group">
              <label className="form-label">CPSC ID</label>
              <input
                type="text"
                className="form-input"
                placeholder="Search by CPSC ID..."
                value={cpscId}
                onChange={(e) => setCpscId(e.target.value)}
              />
            </div>

            {/* Lab Name */}
            <div className="form-group">
              <label className="form-label">Lab Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Search by lab name..."
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {/* Lab Address */}
            <div className="form-group">
              <label className="form-label">Lab Address</label>
              <input
                type="text"
                className="form-input"
                placeholder="Search by address (line, city, state, country)..."
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>

            {/* Search Button */}
            <div className="form-group">
              <button
                className="modal-btn modal-btn-save"
                onClick={handleSearch}
                disabled={loading}
              >
                {loading ? "Searching..." : "Search"}
              </button>
            </div>
          </div>

          {/* Instruction Line */}
          <div
            style={{
              fontSize: "0.9em",
              margin: "10px 0 6px 0",
              color: "#444",
            }}
          >
            Click <strong>Use This Lab</strong> to select and confirm the testing laboratory.
          </div>

          {/* Results */}
          <div
            className="results-list"
            style={{
              maxHeight: "250px",
              overflowY: "auto",
              marginTop: "5px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "8px",
            }}
          >
            {results.length === 0 ? (
              <div>No labs found</div>
            ) : (
              results.map((lab: any) => (
                <div
                  key={`${lab.source}-${lab.id}`}
                  className="lab-result"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    padding: "8px 0",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <div>
                    <strong>{lab.name}</strong>
                    <div style={{ fontSize: "0.9em", color: "#555" }}>
                      {lab.address_line_1} {lab.city ? `, ${lab.city}` : ""}{" "}
                      {lab.country ? `, ${lab.country}` : ""}
                    </div>
                    {lab.source === "certified" ? (
                      <div style={{ fontSize: "0.8em", color: "green" }}>
                        ✅ CPSC Certified {lab.cpsc_id ? `(${lab.cpsc_id})` : ""}
                      </div>
                    ) : (
                      <div style={{ fontSize: "0.8em", color: "#888" }}>
                        🧪 User-Added Lab
                      </div>
                    )}
                  </div>

                  {/* Use This Lab Button */}
                  <button
                    className="modal-btn modal-btn-save"
                    style={{
                      alignSelf: "flex-start",
                      fontSize: "0.8em",
                      padding: "4px 10px",
                      marginTop: "2px",
                    }}
                    onClick={() => {
                      {
                        onSelectLab(lab); // ✅ now properly typed
                        onClose();
                      }
                    }}
                  >
                    Use This Lab
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="data-modal-footer">
          <button className="modal-btn modal-btn-cancel" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SearchLabsModal;
